/**
 * whisper-receiver.ts — Agent-side playback of supervisor whisper audio.
 *
 * The supervisor's voice is captured in their browser, encoded to mulaw 8kHz,
 * and forwarded to the agent's browser via Socket.IO (NOT through Twilio —
 * see CallStreamGateway.forwardWhisperToAgent). This module decodes the
 * mulaw frames and plays them through a Web Audio AudioContext, mixed with
 * the live call audio the agent is already hearing via the Voice SDK.
 *
 * The lead never hears any of this — supervisor audio never enters Twilio's
 * media path. That's why the whole sidecar exists. See
 * call-stream.gateway.ts:handleMicAudio for the routing decision.
 */

// G.711 mu-law decode: byte → 16-bit PCM sample.
function mulawByteToPcm(mulaw: number): number {
  mulaw = ~mulaw & 0xff;
  const sign = mulaw & 0x80;
  const exponent = (mulaw >> 4) & 0x07;
  const mantissa = mulaw & 0x0f;
  let sample = ((mantissa << 3) + 0x84) << exponent;
  sample -= 0x84;
  return sign ? -sample : sample;
}

function base64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function mulawToFloat32(bytes: Uint8Array): Float32Array {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    out[i] = mulawByteToPcm(bytes[i]) / 32768;
  }
  return out;
}

export interface WhisperReceiverHandle {
  stop: () => void;
  setVolume: (v: number) => void;
}

/**
 * Start playback of an inbound whisper stream. Returns a handle to stop or
 * adjust volume. Safe to call once per active call — frames are queued and
 * scheduled so playback is gap-free even with bursty Socket.IO frame arrival.
 *
 * @param emitter - the Socket.IO socket already connected to /call-stream.
 *                  We attach a `whisper-audio` listener; cleanup detaches it.
 * @param initialVolume - 0..1, default 1.0
 */
export function startWhisperReceiver(
  emitter: { on: (e: string, fn: (...args: any[]) => void) => void; off: (e: string, fn: (...args: any[]) => void) => void },
  initialVolume = 1.0,
): WhisperReceiverHandle {
  const AudioCtx: typeof AudioContext =
    (window.AudioContext as any) || (window as any).webkitAudioContext;
  // latencyHint='interactive' — same reasoning as supervisor side: this is
  // live monitoring, not music playback, so we want the smallest hardware
  // buffer the platform supports.
  const ctx: AudioContext = new AudioCtx({ sampleRate: 8000, latencyHint: 'interactive' });
  const gain = ctx.createGain();
  gain.gain.value = initialVolume;
  gain.connect(ctx.destination);

  // Schedule each incoming frame contiguously on the audio timeline.
  // playheadTime advances by the duration of each frame as we schedule it,
  // so frames play back-to-back without gaps even if they arrive in a burst.
  let playheadTime = ctx.currentTime;
  // Safety margin so we never try to start a buffer in the past — the audio
  // graph silently drops past-scheduled sources on Chrome/Safari and would
  // produce mysterious silence gaps. 20ms is enough to absorb any same-tick
  // scheduling jitter without adding perceptible delay.
  const SAFETY_MARGIN = 0.02;

  // Activity tracking for ducking: when whisper frames are arriving, we
  // dispatch `coach:whisper-active` so other parts of the app (Twilio call
  // audio) can lower their volume. After 500ms of no frames we dispatch
  // `coach:whisper-inactive` to restore. This makes the supervisor's voice
  // cut through the call audio without us having to make whisper itself
  // loud or jarring.
  let isActive = false;
  let silenceTimer: ReturnType<typeof setTimeout> | null = null;
  const SILENCE_MS = 500;

  const setActive = (next: boolean) => {
    if (isActive === next) return;
    isActive = next;
    try {
      window.dispatchEvent(new CustomEvent(next ? 'coach:whisper-active' : 'coach:whisper-inactive'));
    } catch { /* ignore */ }
  };

  const handler = (msg: any) => {
    if (!msg || typeof msg.payload !== 'string') return;
    let pcm: Float32Array;
    try {
      pcm = mulawToFloat32(base64ToUint8(msg.payload));
    } catch {
      return;
    }
    if (pcm.length === 0) return;

    // Browsers start AudioContext in 'suspended' if created before any user
    // gesture. The agent has interacted with the page by the time they're
    // on a call (accepted incoming, or clicked dial), so resume() should
    // succeed. Do this each frame as a safety net — it's a no-op when
    // already running.
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* ignore */ });
    }

    const buffer = ctx.createBuffer(1, pcm.length, 8000);
    buffer.getChannelData(0).set(pcm);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(gain);

    // If we've fallen behind (network blip, GC pause), re-anchor to now
    // plus a small safety margin. Without the margin, scheduling at
    // exactly `now` races the audio thread and silently drops the buffer.
    const now = ctx.currentTime;
    if (playheadTime < now + SAFETY_MARGIN) playheadTime = now + SAFETY_MARGIN;
    src.start(playheadTime);
    playheadTime += pcm.length / 8000;

    // Mark active + reset silence timer
    setActive(true);
    if (silenceTimer) clearTimeout(silenceTimer);
    silenceTimer = setTimeout(() => setActive(false), SILENCE_MS);
  };

  emitter.on('whisper-audio', handler);

  return {
    stop: () => {
      emitter.off('whisper-audio', handler);
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
      }
      setActive(false);
      try {
        gain.disconnect();
      } catch { /* ignore */ }
      ctx.close().catch(() => { /* ignore */ });
    },
    setVolume: (v: number) => {
      gain.gain.value = Math.max(0, Math.min(1.5, v));
    },
  };
}
